import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import Header from 'components/headers/withGreenBack'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import { faFull, faPlain, toman } from 'utilities/helper/format'
import { drawCashbox, getCashboxDrawables } from 'services/cashbox'
import {
    Card,
    Notice,
    PrimaryButton,
    SecondaryButton,
    apiMessage,
} from 'modules/cashbox/ui'
import { CASHBOX_KEY, useCashbox, useCashboxMembers } from 'modules/cashbox/hooks'

type Mode = 'ato' | 'mnl'

/**
 * Stable analytics names for the guards below.
 *
 * The manager-facing blocker text carries the box's balance, the required
 * amount and how many members still owe — none of which belongs in PostHog, and
 * all of which would make the same blocker look like a hundred different ones.
 * The code is what is sent; the Persian sentence stays on screen.
 */
type BlockerCode =
    | 'not_active'
    | 'balance_mismatch'
    | 'unpaid_members'
    | 'too_few_chances'
    | 'wrong_winner_count'

interface Blocker {
    code: BlockerCode
    message: string
}

const keyOf = (row: ICashboxDrawable) => `${row.type}-${row.id}`

/**
 * قرعه‌کشی — the draw.
 *
 * Two modes, both from Android:
 *   - `ato`: the backend picks at random. The manager may narrow the pool
 *     first; every drawable is ticked by default.
 *   - `mnl`: the manager names the winners outright.
 *
 * Three guards here exist because the backend does not have them:
 *
 *  1. **Never submit fewer drawables than `number_of_loans`.** The view sets
 *     `winners_count = number_of_loans` when the body omits it (Android never
 *     sends it), then calls `random.sample(pool, winners_count)` — which raises
 *     ValueError, i.e. a 500, when the pool is smaller. Narrowing the auto pool
 *     to two people in a three-loan box is enough to hit it.
 *  2. **Refuse to draw unless the box is financially satisfied.**
 *     `is_financially_satisfied()` compares balance to `satisfactory_amount`
 *     with `==`, and a draw attempted below it comes back as an opaque code.
 *  3. **A share group counts as one drawable, a member as one per unwon
 *     share** — mirroring `get_weighted_drawable_list`, so the odds shown here
 *     are the odds actually used.
 */
export default function CashboxDrawPage() {
    const { id } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { cashbox, isLoading: boxLoading } = useCashbox(id)
    const { data: membersData } = useCashboxMembers(id)
    const {
        data,
        isLoading,
        isError,
        refetch,
    } = useQuery(['cashbox-drawables', id], () => getCashboxDrawables(id!), {
        enabled: Boolean(id),
    })

    const [mode, setMode] = useState<Mode>('ato')
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [result, setResult] = useState<Array<any> | null>(null)

    const drawables = useMemo(() => data?.results ?? [], [data])

    // Auto mode starts with the whole pool ticked, which is both the Android
    // default and the only selection guaranteed to be large enough.
    useEffect(() => {
        if (mode === 'ato') setSelected(new Set(drawables.map(keyOf)))
        else setSelected(new Set())
    }, [mode, drawables])

    const period = cashbox?.period
    const numberOfLoans = period?.number_of_loans ?? 1

    const unpaidMembers = useMemo(
        () =>
            (membersData?.memberships ?? []).filter(
                (member) => member.remaining_share_amount > 0
            ),
        [membersData]
    )

    const notSatisfied =
        (cashbox?.balance ?? 0) !== (period?.satisfactory_amount ?? 0)

    /**
     * How many chances the current selection represents. A membership row can
     * stand for several shares, so this is not the number of ticked rows.
     */
    const selectedChances = useMemo(
        () =>
            drawables
                .filter((row) => selected.has(keyOf(row)))
                .reduce(
                    (sum, row) =>
                        sum +
                        (row.type === 'share_group'
                            ? 1
                            : row.non_won_shares ?? 1),
                    0
                ),
        [drawables, selected]
    )

    const blockers: Array<Blocker> = []
    if (cashbox?.state !== 'act')
        blockers.push({ code: 'not_active', message: 'صندوق فعال نیست' })
    if (notSatisfied)
        blockers.push({
            code: 'balance_mismatch',
            message: `موجودی صندوق (${toman(
                cashbox?.balance ?? 0
            )}) با مبلغ لازم (${toman(
                period?.satisfactory_amount ?? 0
            )}) برابر نیست`,
        })
    if (unpaidMembers.length > 0)
        blockers.push({
            code: 'unpaid_members',
            message: `${faPlain.format(
                unpaidMembers.length
            )} عضو هنوز سهم این نوبت را کامل نپرداخته‌اند`,
        })
    if (mode === 'ato' && selectedChances < numberOfLoans)
        blockers.push({
            code: 'too_few_chances',
            message: `برای ${faPlain.format(
                numberOfLoans
            )} وام، حداقل ${faPlain.format(
                numberOfLoans
            )} شانس باید در قرعه‌کشی باشد (اکنون ${faPlain.format(
                selectedChances
            )})`,
        })
    if (mode === 'mnl' && selected.size !== numberOfLoans)
        blockers.push({
            code: 'wrong_winner_count',
            message: `دقیقاً ${faPlain.format(numberOfLoans)} برنده انتخاب کنید`,
        })

    // The gate is the story: `cashbox_draw_done` has never fired once, and the
    // draw button is disabled while any of the guards above holds. Without this
    // event a box that can never draw and a box nobody tried to draw look
    // identical from the outside.
    //
    // Keyed on the joined codes rather than the array, which is rebuilt on every
    // render, so a manager sitting on the same blocker sends one event, not one
    // per keystroke elsewhere on the screen.
    //
    // Held back until all three queries have answered: mid-load the box has no
    // state and no members, which reads as `not_active` and would report a
    // blocker nobody ever saw.
    const blockerKey = blockers.map((blocker) => blocker.code).join(',')
    const ready = Boolean(cashbox) && Boolean(membersData) && !isLoading
    useEffect(() => {
        if (!ready || !blockerKey) return
        posthog.capture('cashbox_draw_blocked', {
            cashbox_id: id,
            mode,
            blocker_codes: blockerKey.split(','),
        })
    }, [ready, blockerKey, id, mode])

    // Same reason, from the other side: a member who walks into قرعه‌کشی and is
    // turned away is a UX signal, not a no-op. In an effect rather than in the
    // early-return branch below so re-renders do not each count as a visit.
    const notPermitted = Boolean(cashbox) && cashbox?.membership?.role !== 'own'
    const role = cashbox?.membership?.role
    useEffect(() => {
        if (!notPermitted) return
        posthog.capture('cashbox_draw_not_permitted', {
            cashbox_id: id,
            role,
        })
    }, [notPermitted, id, role])

    const draw = useMutation({
        mutationFn: () => {
            const chosen = drawables.filter((row) => selected.has(keyOf(row)))
            if (mode === 'mnl')
                return drawCashbox({
                    id: id!,
                    type: 'mnl',
                    winners: chosen.map((row) => ({
                        id: row.id,
                        winner_type: row.type,
                    })),
                })
            return drawCashbox({
                id: id!,
                type: 'ato',
                selected_drawables: chosen.map((row) => ({
                    id: row.id,
                    type: row.type,
                })),
            })
        },
        onSuccess: (response: any) => {
            const winners = response?.results?.winners ?? []
            // `draw` answers 200 even when it refuses — the refusal is a code
            // in the body (1201 inactive box, 1202 no open cycle, …) — so
            // "did it work" has to be read off the payload. Counting every
            // onSuccess as a draw would have reported successes that never
            // picked a winner.
            if (winners.length > 0)
                posthog.capture('cashbox_draw_done', {
                    cashbox_id: id,
                    mode,
                    pool_size: selected.size,
                    winners_count: winners.length,
                })
            else
                posthog.capture('cashbox_draw_failed', {
                    cashbox_id: id,
                    mode,
                    response_code: response?.code,
                })
            setResult(winners)
            queryClient.invalidateQueries(CASHBOX_KEY(id))
            queryClient.invalidateQueries(['cashbox-winners', id])
            queryClient.invalidateQueries(['cashbox-drawables', id])
            queryClient.invalidateQueries(['get-boxes'])
        },
        onError: (error: any) => {
            toast.error(apiMessage(error))
        },
    })

    if (boxLoading || isLoading) return <DotsLoading />
    if (isError)
        return (
            <div className="bg-white min-h-screen">
                <Header title="قرعه‌کشی" />
                <div className="p-4">
                    <SectionError onRetry={() => refetch()} />
                </div>
            </div>
        )

    if (cashbox?.membership?.role !== 'own')
        return (
            <div className="bg-white min-h-screen">
                <Header title="قرعه‌کشی" />
                <div className="p-4">
                    <Notice tone="warn">
                        فقط مدیر صندوق می‌تواند قرعه‌کشی کند.
                    </Notice>
                </div>
            </div>
        )

    if (result)
        return (
            <div className="bg-[#FAFAFA] min-h-screen">
                <div className="bg-white">
                    <Header title="نتیجه‌ی قرعه‌کشی" />
                </div>
                <div className="px-4 pt-4">
                    <Notice tone="info">
                        قرعه‌کشی انجام شد. حالا باید وام برندگان پرداخت شود.
                    </Notice>
                    <div className="mt-3">
                        {result.map((winner: any, index: number) => (
                            <Card key={index} className="mb-2">
                                <p className="text-sm font-bold text-black">
                                    {winner?.name ||
                                        winner?.membership?.name ||
                                        winner?.share_group?.name ||
                                        'برنده'}
                                </p>
                                <p className="text-xs text-text-secondary mt-1">
                                    مبلغ وام: {toman(winner?.loan_amount ?? 0)}
                                </p>
                            </Card>
                        ))}
                    </div>
                    <div className="mt-4">
                        <PrimaryButton
                            onClick={() => navigate(`/cashbox/${id}/pay-winners`)}
                        >
                            پرداخت وام برندگان
                        </PrimaryButton>
                    </div>
                    <div className="mt-2">
                        <SecondaryButton
                            onClick={() => navigate(`/cashbox/${id}`)}
                        >
                            بازگشت به صندوق
                        </SecondaryButton>
                    </div>
                </div>
            </div>
        )

    return (
        <div className="bg-[#FAFAFA] min-h-screen pb-8">
            <div className="bg-white">
                <Header title="قرعه‌کشی" />
            </div>

            <div className="px-4 pt-3">
                <Card className="mb-3">
                    <div className="flex justify-between items-baseline">
                        <span className="text-sm text-text-secondary">
                            نوبت جاری
                        </span>
                        <span className="text-sm font-bold text-black">
                            {faPlain.format(period?.cycle_index ?? 0)}
                        </span>
                    </div>
                    <div className="flex justify-between items-baseline mt-2">
                        <span className="text-sm text-text-secondary">
                            تعداد برنده در این نوبت
                        </span>
                        <span className="text-sm text-black">
                            {faPlain.format(numberOfLoans)}
                        </span>
                    </div>
                    <div className="flex justify-between items-baseline mt-2">
                        <span className="text-sm text-text-secondary">
                            مبلغ هر وام
                        </span>
                        <span className="text-sm text-black">
                            {toman(period?.loan_amount ?? 0)}
                        </span>
                    </div>
                </Card>

                <div className="flex gap-2 mb-3">
                    {(
                        [
                            { value: 'ato', label: 'قرعه‌کشی خودکار' },
                            { value: 'mnl', label: 'انتخاب دستی' },
                        ] as Array<{ value: Mode; label: string }>
                    ).map((option) => (
                        <button
                            key={option.value}
                            type="button"
                            onClick={() => setMode(option.value)}
                            className={`flex-1 h-11 rounded-[10px] border text-sm ${
                                mode === option.value
                                    ? 'border-green-1 bg-[#E6F3DD] text-green-1 font-bold'
                                    : 'border-divider bg-white text-text-body'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>

                <p className="text-xs text-text-secondary mb-2">
                    {mode === 'ato'
                        ? 'همه‌ی افراد زیر در قرعه‌کشی هستند. اگر بخواهید می‌توانید بعضی را کنار بگذارید.'
                        : `دقیقاً ${faPlain.format(
                              numberOfLoans
                          )} برنده انتخاب کنید.`}
                </p>

                {drawables.length === 0 ? (
                    <p className="text-sm text-text-secondary text-center py-8">
                        کسی برای قرعه‌کشی باقی نمانده است
                    </p>
                ) : (
                    drawables.map((row) => {
                        const key = keyOf(row)
                        const isSelected = selected.has(key)
                        return (
                            <label
                                key={key}
                                className="flex items-center gap-3 bg-white border border-divider rounded-card p-3 mb-2 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() =>
                                        setSelected((prev) => {
                                            const next = new Set(prev)
                                            if (next.has(key)) next.delete(key)
                                            else next.add(key)
                                            return next
                                        })
                                    }
                                    className="w-4 h-4 accent-green-1"
                                />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-black truncate">
                                        {row.name?.trim() || 'بدون نام'}
                                        {row.type === 'share_group' && (
                                            <span className="text-xs text-text-secondary mr-1">
                                                (گروه خرده‌سهم)
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-text-secondary mt-0.5">
                                        {row.type === 'share_group'
                                            ? '۱ شانس'
                                            : `${faPlain.format(
                                                  row.non_won_shares ?? 1
                                              )} شانس`}
                                        {typeof row.chance === 'number' &&
                                            ` · احتمال ${faFull.format(
                                                Math.round(row.chance * 100)
                                            )}٪`}
                                    </p>
                                </div>
                            </label>
                        )
                    })
                )}

                {blockers.length > 0 && (
                    <div className="mt-3">
                        <Notice tone="warn">
                            {blockers.map((blocker) => (
                                <span key={blocker.code}>
                                    • {blocker.message}
                                    <br />
                                </span>
                            ))}
                        </Notice>
                    </div>
                )}

                <div className="mt-4">
                    <PrimaryButton
                        disabled={blockers.length > 0 || draw.isLoading}
                        onClick={() => {
                            if (
                                !window.confirm(
                                    mode === 'ato'
                                        ? 'قرعه‌کشی انجام شود؟ این کار برگشت‌پذیر نیست.'
                                        : 'برندگان انتخاب‌شده ثبت شوند؟ این کار برگشت‌پذیر نیست.'
                                )
                            )
                                return
                            // After the confirm, so an abandoned confirm dialog
                            // is not counted as an attempt.
                            posthog.capture('cashbox_draw_started', {
                                cashbox_id: id,
                                mode,
                                pool_size: selected.size,
                                selected_chances: selectedChances,
                                number_of_loans: numberOfLoans,
                            })
                            draw.mutate()
                        }}
                    >
                        {draw.isLoading
                            ? 'در حال قرعه‌کشی…'
                            : mode === 'ato'
                            ? 'قرعه‌کشی'
                            : 'ثبت برندگان'}
                    </PrimaryButton>
                </div>
            </div>
        </div>
    )
}
